import { type EnvironmentProviders, type Provider } from '@angular/core';
import {
  DRIVE_AUTH_CONTEXT,
  DriveCompareService,
  DriveSurfaceService,
  DriveSurfaceViewService,
  provideDrive,
  provideDriveCompareDataSource,
  provideDriveConversationPermissions,
  provideDriveErrorHandler,
  provideDriveUnavailableActions,
  provideDriveSurfaceDataSource,
  provideDriveSurfaceViewDataSource,
  provideDriveTranslateFn,
  provideDriveUploadOrchestrator,
} from '@mtm/drive';
import driveTranslations from '@mtm/drive/i18n/en';

import { environment } from '../../../environments/environment';
import { DriveAuthContextService } from './drive-auth-context.service';
import { DriveConversationPermissionsService } from './drive-conversation-permissions.service';
import { DriveErrorHandlerAdapter } from './drive-error-handler.adapter';
import { DriveUnavailableActionsAdapter } from './drive-unavailable-actions.adapter';

const translations = driveTranslations as Record<string, string>;

/**
 * Wires the `@mtm/drive` core into this NgModule app. Spread into
 * `AppModule.providers`. Mirrors the drive-fe playground: the HTTP data source
 * reuses the IAM app token (see the Drive-scoped `mtmAppTokenInterceptor` in
 * `AppModule`), and `DRIVE_AUTH_CONTEXT` aliases the app's
 * {@link DriveAuthContextService} singleton.
 *
 * Only drive-core lives here — it must stay in the root injector because
 * root-scoped services consume it (`DriveProjectStructureService` /
 * `PlatformTaskDataService` inject `DRIVE_DATA_SOURCE`, and the library's own
 * root services resolve `DRIVE_UPLOAD_ADAPTER` / the translate fn from the root
 * environment injector). The `@mtm/media-studio` half (preview, overlays,
 * conversation feed) is in `provideMtmDriveSurface()` on the lazy
 * `ProjectRootModule`, keeping it out of the initial bundle.
 */
export function provideMtmDrive(): (Provider | EnvironmentProviders)[] {
  return [
    provideDriveTranslateFn((key: string, params?: Record<string, string>): string => {
      let text = translations[key] ?? key;
      if (params) {
        for (const [k, v] of Object.entries(params)) {
          text = text.replace(`{${k}}`, v);
        }
      }
      return text;
    }),
    // skipUploadHashing: hashing is ON by default (playground parity) — the SHA-256
    // enables server-side dedup: a byte-identical re-upload gets DEDUP_HINT from
    // init and skips the GCS transfer entirely. `?force-upload=1` skips hashing to
    // force a real upload, same escape hatch as the playground. The package ships
    // its worker under @mtm/drive/workers/, and `webWorkerTsConfig` in angular.json
    // is what makes webpack bundle the library's `new Worker(new URL(...))` — the
    // SDK falls back to an un-hashed upload if hashing ever fails. Requires an
    // @mtm/drive release that contains workers/ (dev.57 does not — bump with this).
    // surfaceViewerUrl: the address of the read-only guest viewer for a shared
    // surface, as a `{id}` template resolved against the current origin. It matches
    // the library default, but is stated here so the coupling with the host-owned
    // `s/:id` route in `app.routes.ts` is visible from both ends — the library only
    // renders this address (config panel "Share link URL", invite emails), it never
    // routes to it.
    ...provideDrive({
      apiUrl: environment.drive.apiUrl,
      surfaceViewerUrl: '/s/{id}',
      skipUploadHashing: new URLSearchParams(window.location.search).get('force-upload') === '1',
    }),
    ...provideDriveUploadOrchestrator(),
    { provide: DRIVE_AUTH_CONTEXT, useExisting: DriveAuthContextService },
    provideDriveErrorHandler(DriveErrorHandlerAdapter),
    provideDriveUnavailableActions(DriveUnavailableActionsAdapter),
    // CORE_CONVERSATION from the app's effective permissions: the audience manager offers to
    // create each audience type only where `create` is granted (the rest follows audience roles).
    // Root, so the project explorer and the org-level comment shells read the same rights.
    provideDriveConversationPermissions(DriveConversationPermissionsService),

    // https://github.com/MTMVideo/drive-fe/pull/87/changes#diff-7886b540a1aea70606528d19fd7de27560f56a554d53d2169ac84421fd972028R187
    provideDriveCompareDataSource(DriveCompareService),
    provideDriveSurfaceViewDataSource(DriveSurfaceViewService),

    // Shared surfaces — SHARE_LINK (kind 2) and RELEASE (kind 12). This is the whole
    // gate for share links: the explorer injects the token `{ optional: true }` and
    // throws only when a surface operation is attempted, so a host that never opts in
    // (`showSurfaceSections`, see `drive-project-surface.component.html`) is unaffected.
    // Must stay in the ROOT injector — both the project explorer and the `s/:id` guest
    // viewer route resolve it.
    provideDriveSurfaceDataSource(DriveSurfaceService),
  ];
}
